<template>
  <div class="seller-add-product-wrapper">
    <!-- Header Hero avec Glassmorphism (gardé) -->
    <div class="product-hero">
      <div class="hero-content">
        <div class="hero-icon-wrapper">
          <v-icon size="32" color="white">mdi-store-plus</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="title font-weight-bold white--text mb-1">
            {{ isEditMode ? 'Edit Product' : 'Add New Product' }}
          </h1>
          <p class="caption white--text mb-0" style="opacity: 0.9;">
            {{ isEditMode ? 'Update your listing details and media' : 'List your item in 4 simple steps' }}
          </p>
        </div>
      </div>
      <div class="hero-decoration"></div>
    </div>

    <!-- ────────────── Carte Stepper (design "manager") ────────────── -->
    <v-container class="form-container pa-4">
      <v-row justify="center" class="ma-0">
        <v-col cols="12" lg="9" xl="8">
          <div class="d-flex align-center mb-3">
            <v-btn icon small class="mr-2" @click="$router.back()">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="caption grey--text">Back</span>
          </div>

          <v-card class="add-product-card elevation-3">
            <!-- ── STEP HEADER (progress + labels) ── -->
            <v-sheet color="grey lighten-5" class="px-4 pt-4 pb-2">
              <v-progress-linear
                :value="(currentStep / totalSteps) * 100"
                color="primary"
                background-color="grey lighten-3"
                height="6"
                class="mb-3 app-pl"
              ></v-progress-linear>

              <div class="d-flex justify-space-between step-header">
                <div
                  v-for="(step, i) in steps"
                  :key="i"
                  class="step-item d-flex flex-column align-center"
                  :class="{ 'step-active': currentStep === i + 1, 'step-done': currentStep > i + 1 }"
                  @click="goToStep(i + 1)"
                  style="cursor:pointer; flex:1"
                >
                  <div class="step-circle">
                    <v-icon v-if="currentStep > i + 1" size="18" color="white">mdi-check</v-icon>
                    <span v-else class="font-weight-bold">{{ i + 1 }}</span>
                  </div>
                  <span class="step-label text-center">{{ step.label }}</span>
                </div>
              </div>
            </v-sheet>

            <v-divider></v-divider>

            <!-- ── STEP 1 — Basic Info ── -->
            <transition name="slide-fade" mode="out-in">
              <div v-if="currentStep === 1" key="step1" class="pa-4 step-body">
                <div class="mb-4">
                  <div class="d-flex align-center mb-1">
                    <v-icon color="primary" class="mr-2" size="20">mdi-information-outline</v-icon>
                    <span class="title font-weight-bold">Basic Information</span>
                  </div>
                  <p class="caption grey--text ma-0">Name, category and supplier contact</p>
                  <v-divider class="mt-2 mb-4"></v-divider>
                </div>

                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="selectedCategory"
                      :items="categoryItems"
                      item-text="name"
                      item-value="id"
                      outline dense
                      :loading="categoriesLoading"
                      :no-data-text="categoriesLoading ? 'Loading…' : 'No categories'"
                      label="Product Category *"
                      prepend-inner-icon="mdi-tag-outline"
                      :error-messages="errors.selectedCategory"
                      @change="clearError('selectedCategory')"
                    ></v-select>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="product_name"
                      outline dense
                      label="Product Name *"
                      prepend-inner-icon="mdi-package-variant"
                      :error-messages="errors.product_name"
                      @input="clearError('product_name')"
                      counter="120"
                      maxlength="120"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="supplier_contact"
                      outline dense
                      label="WhatsApp Inquiry Contact *"
                      prepend-inner-icon="mdi-whatsapp"
                      type="tel"
                      hint="Full number with country code e.g. 237691234567"
                      persistent-hint
                      :error-messages="errors.supplier_contact"
                      @input="clearError('supplier_contact')"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="key_words"
                      outline dense
                      label="Keywords *"
                      prepend-inner-icon="mdi-tag-multiple-outline"
                      hint="Separate with commas: shoes, men, sport"
                      persistent-hint
                      :error-messages="errors.key_words"
                      @input="clearError('key_words')"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>
            </transition>

            <!-- ── STEP 2 — Pricing & Stock ── -->
            <transition name="slide-fade" mode="out-in">
              <div v-if="currentStep === 2" key="step2" class="pa-4 step-body">
                <div class="mb-4">
                  <div class="d-flex align-center mb-1">
                    <v-icon color="primary" class="mr-2" size="20">mdi-currency-usd</v-icon>
                    <span class="title font-weight-bold">Pricing &amp; Stock</span>
                  </div>
                  <p class="caption grey--text ma-0">Set your selling price, buy price and available stock</p>
                  <v-divider class="mt-2 mb-4"></v-divider>
                </div>

                <v-row dense>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="product_price"
                      outline dense
                      label="Selling Price (XAF) *"
                      type="number"
                      min="0"
                      prepend-inner-icon="mdi-cash"
                      :error-messages="errors.product_price"
                      @input="clearError('product_price')"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="product_wholesale_price"
                      outline dense
                      label="Buy Price (XAF) *"
                      type="number"
                      min="0"
                      prepend-inner-icon="mdi-cash-minus"
                      :error-messages="errors.product_wholesale_price"
                      @input="clearError('product_wholesale_price')"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="product_no_stock"
                      outline dense
                      label="Number in Stock *"
                      type="number"
                      min="0"
                      prepend-inner-icon="mdi-package-variant-closed"
                      :error-messages="errors.product_no_stock"
                      @input="clearError('product_no_stock')"
                    ></v-text-field>
                  </v-col>

                  <!-- Margin indicator -->
                  <v-col cols="12" v-if="margin !== null">
                    <v-alert
                      :type="margin >= 0 ? 'success' : 'warning'"
                      outline
                      class="margin-alert"
                      icon="mdi-chart-line"
                    >
                      <strong>Margin:</strong>
                      {{ formatPrice(Math.abs(margin)) }}
                      {{ margin >= 0 ? 'profit' : 'loss' }} per unit
                      <span v-if="product_price && product_wholesale_price" class="ml-1">
                        ({{ marginPct }}%)
                      </span>
                    </v-alert>
                  </v-col>

                  <!-- Commission Pettycash -->
                  <v-col cols="12">
                    <v-alert
                      :type="commissionConfig ? 'info' : 'grey'"
                      outline
                      class="commission-alert"
                      icon="mdi-percent"
                    >
                      <div class="d-flex align-center justify-space-between flex-wrap">
                        <div>
                          <div class="font-weight-bold mb-1">Pettycash commission</div>
                          <div v-if="commissionConfig" class="caption mb-0">
                            {{ commissionLabel }} - deducted from product price (excluding delivery).
                          </div>
                          <div v-else class="caption mb-0 grey--text">
                            Loading commission configuration...
                          </div>
                          <div v-if="commissionConfig" class="caption mt-1 mb-0">
                            {{ commissionPriceLabel }}: commission {{ commissionAmountPerUnit }} XAF,
                            you receive {{ sellerNetPerUnit }} XAF.
                          </div>
                        </div>
                        <v-btn
                          small
                          flat
                          color="primary"
                          :loading="commissionLoading"
                          @click="loadCommissionConfig"
                        >
                          Refresh
                        </v-btn>
                      </div>
                    </v-alert>
                  </v-col>
                </v-row>
              </div>
            </transition>

            <!-- ── STEP 3 — Media ── -->
            <transition name="slide-fade" mode="out-in">
              <div v-if="currentStep === 3" key="step3" class="pa-4 step-body">
                <div class="mb-4">
                  <div class="d-flex align-center mb-1">
                    <v-icon color="primary" class="mr-2" size="20">mdi-image-multiple-outline</v-icon>
                    <span class="title font-weight-bold">Product Media</span>
                  </div>
                  <p class="caption grey--text ma-0">Cover image, extra photos and optional video</p>
                  <v-divider class="mt-2 mb-4"></v-divider>
                </div>

                <!-- Cover image -->
                <div class="media-zone pa-3 mb-3" :class="{ 'media-zone--error': errors.product_cover_image }">
                  <p class="caption font-weight-bold text-uppercase grey--text mb-2">
                    <v-icon size="14" class="mr-1">mdi-image-outline</v-icon>Cover Image *
                  </p>
                  <div v-if="coverPreview || existingCoverUrl" class="cover-preview-wrap mb-2">
                    <v-img :src="coverPreview || existingCoverUrl" height="160" contain class="rounded-corners grey lighten-4"></v-img>
                    <v-chip small dark :color="coverPreview ? 'success' : 'primary'" class="cover-label">
                      {{ coverPreview ? 'New Cover' : 'Current Cover' }}
                    </v-chip>
                    <v-btn v-if="coverPreview" fab small color="error" elevation="2" class="cover-remove-btn"
                      @click="removeCover">
                      <v-icon size="14">mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <v-btn block outline color="primary" class="text-none font-weight-bold" @click="$refs.coverInput.click()">
                    <v-icon left size="16">mdi-upload</v-icon>Choose cover image
                  </v-btn>
                  <input ref="coverInput" type="file" accept="image/*" class="hidden-input" @change="onCoverChange">
                </div>

                <!-- Video (optional) -->
                <div class="media-zone pa-3 mb-3">
                  <p class="caption font-weight-bold text-uppercase grey--text mb-2">
                    <v-icon size="14" class="mr-1">mdi-video-outline</v-icon>Product Video
                    <v-chip x-small color="grey lighten-3" class="ml-1">Optional</v-chip>
                  </p>
                  <div v-if="videoPreview || existingVideoUrl" class="mb-2">
                    <video :src="videoPreview || existingVideoUrl" controls height="160"
                      style="width:100%;border-radius:8px;background:#000" preload="metadata"></video>
                    <div class="d-flex align-center mt-1">
                      <v-btn v-if="videoPreview" small flat color="error" @click="removeVideo">
                        <v-icon left size="14">mdi-close</v-icon>Remove
                      </v-btn>
                      <v-chip v-else-if="existingVideoUrl" x-small color="grey lighten-3" class="ml-1">Current video</v-chip>
                    </div>
                  </div>
                  <v-btn block outline color="primary" class="text-none font-weight-bold" @click="$refs.videoInput.click()">
                    <v-icon left size="16">mdi-upload</v-icon>Choose product video
                  </v-btn>
                  <input ref="videoInput" type="file" accept="video/*" class="hidden-input" @change="onVideoChange">
                </div>

                <!-- Extra images -->
                <div class="media-zone pa-3" :class="{ 'media-zone--error': errors.product_images }">
                  <p class="caption font-weight-bold text-uppercase grey--text mb-2">
                    <v-icon size="14" class="mr-1">mdi-image-multiple-outline</v-icon>Additional Images *
                  </p>

                  <!-- Existing gallery (edit mode) -->
                  <v-row v-if="existingGallery.length" dense class="mb-2">
                    <v-col v-for="(img, idx) in existingGallery" :key="'existing-' + idx" cols="3" sm="2" md="1">
                      <v-img :src="img.get_image || img" height="56" contain class="rounded-corners grey lighten-4"></v-img>
                    </v-col>
                  </v-row>

                  <!-- New previews -->
                  <div v-if="imagePreviews.length" class="d-flex flex-wrap mb-2" style="gap:8px">
                    <div
                      v-for="(src, idx) in imagePreviews"
                      :key="idx"
                      class="extra-thumb-wrap"
                    >
                      <v-img :src="src" width="72" height="72" contain class="rounded-corners grey lighten-4"></v-img>
                      <v-btn fab small color="error" elevation="1" class="thumb-remove-btn"
                        @click="removeExtraImage(idx)">
                        <v-icon size="10">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <v-btn block outline color="primary" class="text-none font-weight-bold" @click="$refs.imagesInput.click()">
                    <v-icon left size="16">mdi-upload</v-icon>Choose product images
                  </v-btn>
                  <input ref="imagesInput" type="file" accept="image/*" multiple class="hidden-input" @change="onImagesChange">
                  <p class="caption grey--text mt-1 mb-0">You can select multiple images at once</p>
                </div>
              </div>
            </transition>

            <!-- ── STEP 4 — Description & Review ── -->
            <transition name="slide-fade" mode="out-in">
              <div v-if="currentStep === 4" key="step4" class="pa-4 step-body">
                <div class="mb-4">
                  <div class="d-flex align-center mb-1">
                    <v-icon color="primary" class="mr-2" size="20">mdi-text-box-outline</v-icon>
                    <span class="title font-weight-bold">Description &amp; Review</span>
                  </div>
                  <p class="caption grey--text ma-0">Write a compelling product description then review before publishing</p>
                  <v-divider class="mt-2 mb-4"></v-divider>
                </div>

                <!-- Description -->
                <div class="mb-4">
                  <p class="body-2 font-weight-bold mb-2">
                    Product Description *
                    <v-chip x-small outline color="grey" class="ml-1">{{ descriptionLength }} chars</v-chip>
                  </p>
                  <div class="rich-editor-wrapper">
                    <div class="editor-toolbar">
                      <v-btn small flat @click="execCmd('bold')" title="Bold">
                        <v-icon small>mdi-format-bold</v-icon>
                      </v-btn>
                      <v-btn small flat @click="execCmd('italic')" title="Italic">
                        <v-icon small>mdi-format-italic</v-icon>
                      </v-btn>
                      <v-btn small flat @click="execCmd('underline')" title="Underline">
                        <v-icon small>mdi-format-underline</v-icon>
                      </v-btn>
                      <v-btn small flat @click="execCmd('insertUnorderedList')" title="Bullet List">
                        <v-icon small>mdi-format-list-bulleted</v-icon>
                      </v-btn>
                      <v-btn small flat @click="execCmd('insertOrderedList')" title="Numbered List">
                        <v-icon small>mdi-format-list-numbered</v-icon>
                      </v-btn>
                    </div>
                    <div
                      ref="descriptionEditor"
                      class="rich-editor"
                      contenteditable="true"
                      @input="onDescriptionInput"
                      placeholder="Describe your product features, benefits, and specifications..."
                    ></div>
                    <div class="editor-footer d-flex justify-space-between align-center px-3 py-2">
                      <span class="caption grey--text">Tip: Good descriptions increase conversion</span>
                      <span class="caption" :class="descriptionLength < 100 ? 'error--text' : 'success--text'">
                        {{ descriptionLength < 100 ? 'Min 100 characters recommended' : 'Good length' }}
                      </span>
                    </div>
                  </div>
                  <p v-if="errors.product_description" class="error--text caption mt-1">
                    {{ errors.product_description }}
                  </p>
                </div>

                <v-divider class="mb-4"></v-divider>
                <p class="review-title font-weight-bold text-uppercase grey--text mb-3">
                  <v-icon size="16" class="mr-1">mdi-eye-outline</v-icon>Review Summary
                </p>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <div class="d-flex align-start py-1">
                      <span class="caption grey--text mr-2" style="min-width:80px;flex-shrink:0">Name:</span>
                      <span class="caption font-weight-bold primary--text text-truncate" style="max-width:200px">{{ product_name || '—' }}</span>
                    </div>
                    <div class="d-flex align-start py-1">
                      <span class="caption grey--text mr-2" style="min-width:80px;flex-shrink:0">Category:</span>
                      <span class="caption font-weight-bold primary--text text-truncate" style="max-width:200px">{{ categoryName }}</span>
                    </div>
                    <div class="d-flex align-start py-1">
                      <span class="caption grey--text mr-2" style="min-width:80px;flex-shrink:0">Contact:</span>
                      <span class="caption font-weight-bold primary--text text-truncate" style="max-width:200px">{{ supplier_contact }}</span>
                    </div>
                    <div class="d-flex align-start py-1">
                      <span class="caption grey--text mr-2" style="min-width:80px;flex-shrink:0">Keywords:</span>
                      <span class="caption font-weight-bold primary--text text-truncate" style="max-width:200px">{{ key_words || '—' }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="d-flex align-start py-1">
                      <span class="caption grey--text mr-2" style="min-width:80px;flex-shrink:0">Sell Price:</span>
                      <span class="caption font-weight-bold primary--text text-truncate" style="max-width:200px">{{ formatPrice(product_price) }}</span>
                    </div>
                    <div class="d-flex align-start py-1">
                      <span class="caption grey--text mr-2" style="min-width:80px;flex-shrink:0">Buy Price:</span>
                      <span class="caption font-weight-bold primary--text text-truncate" style="max-width:200px">{{ formatPrice(product_wholesale_price) }}</span>
                    </div>
                    <div class="d-flex align-start py-1">
                      <span class="caption grey--text mr-2" style="min-width:80px;flex-shrink:0">Stock:</span>
                      <span class="caption font-weight-bold primary--text text-truncate" style="max-width:200px">{{ product_no_stock }} units</span>
                    </div>
                    <div class="d-flex align-start py-1">
                      <span class="caption grey--text mr-2" style="min-width:80px;flex-shrink:0">Cover:</span>
                      <span class="caption font-weight-bold primary--text text-truncate" style="max-width:200px">{{ coverName }}</span>
                    </div>
                    <div class="d-flex align-start py-1">
                      <span class="caption grey--text mr-2" style="min-width:80px;flex-shrink:0">Extra imgs:</span>
                      <span class="caption font-weight-bold primary--text text-truncate" style="max-width:200px">{{ extraImagesCount }} file(s)</span>
                    </div>
                    <div class="d-flex align-start py-1">
                      <span class="caption grey--text mr-2" style="min-width:80px;flex-shrink:0">Video:</span>
                      <span class="caption font-weight-bold primary--text text-truncate" style="max-width:200px">{{ videoName }}</span>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </transition>

            <v-divider></v-divider>

            <!-- ── NAV BUTTONS ── -->
            <div class="d-flex align-center justify-space-between pa-4">
              <v-btn
                flat class="text-none font-weight-bold"
                :disabled="currentStep === 1 || loading"
                @click="prevStep"
              >
                <v-icon left size="18">mdi-arrow-left</v-icon>Back
              </v-btn>

              <span class="caption grey--text">Step {{ currentStep }} of {{ totalSteps }}</span>

              <div>
                <v-btn
                  v-if="currentStep === totalSteps"
                  color="success"
                  dark
                  elevation="2"
                  class="text-none font-weight-bold px-4 pill-btn"
                  :loading="loading"
                  @click="submitForm"
                >
                  <v-icon left size="18">mdi-cloud-upload-outline</v-icon>
                  {{ isEditMode ? 'Update Product' : 'Publish Product' }}
                </v-btn>

                <v-btn
                  v-else
                  color="primary"
                  dark
                  elevation="2"
                  class="text-none font-weight-bold px-4 pill-btn"
                  @click="nextStep"
                >
                  Next<v-icon right size="18">mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Dialog de Confirmation -->
    <v-dialog v-model="showConfirm" max-width="400" persistent>
      <v-card class="pa-4 text-center">
        <v-icon size="64" color="success" class="mb-4">mdi-check-circle-outline</v-icon>
        <v-card-title class="justify-center headline mb-2">
          {{ isEditMode ? 'Product Updated!' : 'Product Published!' }}
        </v-card-title>
        <v-card-text class="body-1 grey--text">
          {{ isEditMode ? 'Your changes are now live on the marketplace.' : 'Your product is now live on the marketplace.' }}
        </v-card-text>
        <v-card-actions class="justify-center pt-4">
          <v-btn color="primary" dark rounded class="pill-btn" @click="goToProduct">
            View Product
          </v-btn>
          <v-btn flat class="pill-btn" @click="addAnother">
            Add Another
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'SellerAddProduct',


  data() {
    return {
      currentStep: 1,
      totalSteps: 4,
      steps: [
        { label: 'Basic Info' },
        { label: 'Pricing' },
        { label: 'Media' },
        { label: 'Description' },
      ],

      // Basique
      selectedCategory: '',
      product_name: '',
      supplier_contact: '237',
      key_words: '',
      product_price: '',
      product_wholesale_price: '',
      product_no_stock: '',
      product_description: '',
      categoryItems: [],
      categoriesLoading: false,

      // Médias
      product_cover_image: null,
      product_video: null,
      product_images: [],
      coverPreview: null,
      videoPreview: null,
      imagePreviews: [],

      // Édition
      isEditMode: false,
      editProductId: null,
      existingCoverUrl: null,
      existingGallery: [],
      existingVideoUrl: null,

      // Commission
      commissionConfig: null,
      commissionLoading: false,
      commissionSamplePrice: 10000,

      errors: {},
      loading: false,
      showConfirm: false,
      newProductId: null,
    }
  },

  computed: {
    margin() {
      const sell = parseFloat(this.product_price)
      const buy  = parseFloat(this.product_wholesale_price)
      if (!sell || !buy) return null
      return sell - buy
    },
    marginPct() {
      const sell = parseFloat(this.product_price)
      if (!sell) return 0
      return ((this.margin / sell) * 100).toFixed(0)
    },
    categoryName() {
      const cat = this.categoryItems.find(c => c.id === this.selectedCategory)
      return cat ? cat.name : '—'
    },
    hasCover() {
      return !!(this.product_cover_image || this.existingCoverUrl)
    },
    coverName() {
      if (this.product_cover_image) return this.product_cover_image.name
      if (this.existingCoverUrl) return 'Current cover'
      return '—'
    },
    extraImagesCount() {
      return this.product_images.length + this.existingGallery.length
    },
    videoName() {
      return this.product_video ? this.product_video.name
        : (this.existingVideoUrl ? 'Current video' : 'None')
    },
    descriptionLength() {
      return this.product_description.replace(/<[^>]+>/g, '').length
    },

    // Commission
    commissionType() {
      return this.commissionConfig?.commission_type || 'PERCENT'
    },
    commissionValue() {
      const value = this.commissionConfig?.value
      const parsed = parseFloat(value)
      return Number.isFinite(parsed) ? parsed : 0
    },
    commissionLabel() {
      if (!this.commissionConfig) return 'Loading...'
      if (this.commissionType === 'FIXED') {
        return `${this.commissionValue} XAF per item`
      }
      return `${this.commissionValue}% per item`
    },
    commissionBasePrice() {
      const price = parseFloat(this.product_price)
      if (Number.isFinite(price) && price > 0) return price
      return this.commissionSamplePrice
    },
    commissionAmountPerUnit() {
      if (!this.commissionConfig) return 0
      if (this.commissionType === 'FIXED') return Math.round(this.commissionValue)
      return Math.round((this.commissionBasePrice * this.commissionValue) / 100)
    },
    sellerNetPerUnit() {
      const net = this.commissionBasePrice - this.commissionAmountPerUnit
      return net < 0 ? 0 : Math.round(net)
    },
    commissionPriceLabel() {
      if (!this.product_price) return `Example on ${this.commissionBasePrice} XAF`
      return 'Based on your price'
    },
  },

  mounted() {
    document.title = this.isEditMode ? 'Edit Product | Seller Dashboard' : 'Add Product | Seller Dashboard'
    this.loadCommissionConfig()
    this.getProductCategories().then(() => {
      this.initEditMode()
    })
  },

  methods: {
    // ── Navigation ────────────────────────────────────────────────────
    nextStep() {
      if (!this.validateStep(this.currentStep)) return
      this.currentStep++
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
    },
    prevStep() {
      this.currentStep--
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
    },
    goToStep(step) {
      if (step < this.currentStep) this.currentStep = step
    },

    // ── Validation (per step) ─────────────────────────────────────────
    validateStep(step) {
      this.errors = {}
      let valid = true
      const set = (field, msg) => { this.errors = { ...this.errors, [field]: msg }; valid = false }

      if (step === 1) {
        if (!this.selectedCategory) set('selectedCategory', 'Please select a category.')
        if (!this.product_name.trim()) set('product_name', 'Product name is required.')
        else if (this.product_name.trim().length < 3)
          set('product_name', 'Name must be at least 3 characters.')
        if (!this.supplier_contact)
          set('supplier_contact', 'Please enter the WhatsApp contact.')
        else if (!/^[\d]{9,15}$/.test(String(this.supplier_contact).replace(/\s/g, '')))
          set('supplier_contact', 'Enter a valid phone number (9–15 digits).')
        if (!this.key_words.trim()) set('key_words', 'Please enter at least one keyword.')
      }

      if (step === 2) {
        const price = parseFloat(this.product_price)
        const wprice = parseFloat(this.product_wholesale_price)
        const stock  = parseInt(this.product_no_stock)
        if (!this.product_price || isNaN(price) || price <= 0)
          set('product_price', 'Enter a valid selling price greater than 0.')
        if (!this.product_wholesale_price || isNaN(wprice) || wprice <= 0)
          set('product_wholesale_price', 'Enter a valid buy price greater than 0.')
        if (this.product_no_stock === '' || isNaN(stock) || stock < 0)
          set('product_no_stock', 'Enter a valid stock quantity (0 or more).')
      }

      if (step === 3) {
        if (!this.hasCover) set('product_cover_image', 'A cover image is required.')
        else if (this.product_cover_image) {
          const ext = this.product_cover_image.name.split('.').pop().toLowerCase()
          if (!['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext))
            set('product_cover_image', 'Please upload a valid image (JPG, PNG, WEBP, GIF).')
        }
        if (!this.product_images.length && !this.existingGallery.length)
          set('product_images', 'Please upload at least one additional image.')
      }

      if (step === 4) {
        const stripped = (this.product_description || '').replace(/<[^>]*>/g, '').trim()
        if (!stripped || stripped.length < 20)
          set('product_description', 'Description must be at least 20 characters.')
      }

      if (!valid) {
        this.$store.commit('setSnackBarMessage', 'Please fix the errors before continuing.')
        this.$store.commit('setSnackBarColor', 'orange darken-2')
        this.$store.commit('activateSnackBar', true)
      }
      return valid
    },

    clearError(field) {
      if (this.errors[field]) {
        const e = { ...this.errors }
        delete e[field]
        this.errors = e
      }
    },

    // ── Media handlers ────────────────────────────────────────────────
    onCoverChange(e) {
      this.clearError('product_cover_image')
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      const ext = file.name.split('.').pop().toLowerCase()
      if (!['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) {
        this.errors = { ...this.errors, product_cover_image: 'Please upload a valid image (JPG, PNG, WEBP, GIF).' }
        return
      }
      this.product_cover_image = file
      this.coverPreview = URL.createObjectURL(file)
    },
    removeCover() {
      this.product_cover_image = null
      this.coverPreview = null
    },
    onVideoChange(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (!file.type.startsWith('video/')) return
      this.product_video = file
      this.videoPreview = URL.createObjectURL(file)
    },
    removeVideo() {
      this.product_video = null
      this.videoPreview = null
    },
    onImagesChange(e) {
      this.clearError('product_images')
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      files.forEach(file => {
        this.product_images.push(file)
        this.imagePreviews.push(URL.createObjectURL(file))
      })
    },
    removeExtraImage(idx) {
      this.product_images.splice(idx, 1)
      this.imagePreviews.splice(idx, 1)
    },

    // ── Rich editor ───────────────────────────────────────────────────
    onDescriptionInput() {
      this.product_description = this.$refs.descriptionEditor.innerHTML
    },
    execCmd(command) {
      document.execCommand(command, false, null)
      this.$refs.descriptionEditor.focus()
      this.product_description = this.$refs.descriptionEditor.innerHTML
    },

    // ── Helpers ───────────────────────────────────────────────────────
    formatPrice(price) {
      if (!price) return '0 XAF'
      return new Intl.NumberFormat('en-US').format(price) + ' XAF'
    },
    showSnackbar(msg, color) {
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('activateSnackBar', true)
    },
    handleError(err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'An error occurred'
      this.showSnackbar(msg, 'red darken-4')
    },

    // ── API ───────────────────────────────────────────────────────────
    async loadCommissionConfig() {
      this.commissionLoading = true
      try {
        const res = await axios.get('/api/v1/market_place/commission/config/')
        this.commissionConfig = res.data
      } catch (err) {
        this.commissionConfig = null
      } finally {
        this.commissionLoading = false
      }
    },

    async getProductCategories() {
      this.categoriesLoading = true
      try {
        const res = await axios.get('/api/v1/manager/market_place/get/product/categories/')
        this.categoryItems = res.data
        return res.data
      } catch (err) {
        this.handleError(err)
        return []
      } finally {
        this.categoriesLoading = false
      }
    },

    async initEditMode() {
      const editId = this.$route.query.edit
      if (!editId) return
      const parsedId = parseInt(editId)
      if (!parsedId) return

      this.isEditMode = true
      this.editProductId = parsedId
      document.title = 'Edit Product | Seller Dashboard'
      await this.loadProductForEdit(parsedId)
    },

    async loadProductForEdit(productId) {
      this.loading = true
      try {
        const res = await axios.get(`/api/v1/market_place/seller/products/${productId}/detail/`)
        const data = res.data || {}

        this.product_name = data.name || ''
        this.supplier_contact = data.supplier_contact || '237'
        this.selectedCategory = data.category_id || null
        this.product_description = data.description || ''
        this.product_no_stock = data.quantity || ''
        this.product_wholesale_price = data.wholesale_price || ''
        this.product_price = data.price || ''
        this.key_words = data.key_words || ''
        this.existingCoverUrl = data.get_image || null
        this.existingGallery = Array.isArray(data.product_images) ? data.product_images : []
        if (Array.isArray(data.product_videos) && data.product_videos.length > 0) {
          this.existingVideoUrl = data.product_videos[0]?.get_video || null
        } else {
          this.existingVideoUrl = null
        }

        this.$nextTick(() => {
          if (this.$refs.descriptionEditor) {
            this.$refs.descriptionEditor.innerHTML = this.product_description || ''
          }
        })
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },

    async submitForm() {
      if (!this.validateStep(this.totalSteps)) return

      this.loading = true
      const isEdit = this.isEditMode

      const formData = new FormData()
      formData.append('product_name',           this.product_name)
      formData.append('supplier_contact',       this.supplier_contact)
      formData.append('selectedCategory',       this.selectedCategory)
      formData.append('product_no_stock',       this.product_no_stock)
      formData.append('product_description',    this.product_description)
      formData.append('key_words',              this.key_words)
      formData.append('product_price',          this.product_price)
      formData.append('product_wholesale_price', this.product_wholesale_price)
      if (!isEdit || this.product_cover_image) {
        formData.append('product_cover_image', this.product_cover_image)
      }
      if (this.product_video) {
        formData.append('product_video', this.product_video)
      }
      this.product_images.forEach((file, i) => {
        formData.append('product_images' + String(i), file)
      })
      formData.append('product_images_length', this.product_images.length)

      try {
        if (isEdit) {
          const res = await axios.post(
            `/api/v1/market_place/seller/products/${this.editProductId}/edit/`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          )
          this.newProductId = this.editProductId
          if (res.data?.message) {
            this.showSnackbar(res.data.message, 'success')
          }
        } else {
          const res = await axios.post(
            '/api/v1/market_place/seller/products/add/',
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          )
          this.newProductId = res.data[0].product_id
        }
        this.showConfirm = true
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },

    goToProduct() {
      this.$router.push(`/market_place/items/${this.newProductId}`)
    },

    addAnother() {
      if (this.isEditMode) {
        this.$router.push({ name: 'business_seller.products' })
      } else {
        this.$router.go()
      }
    },
  },
}
</script>

<style scoped>
.seller-add-product-wrapper {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding-bottom: 80px;
}

/* ── Header Hero (cadre violet, gardé) ─────────────────── */
.product-hero {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px 24px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.hero-content {
  display: flex;
  align-items: center;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.hero-icon-wrapper {
  width: 64px;
  height: 64px;
  background: rgba(255,255,255,0.2);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
}

.hero-decoration {
  position: absolute;
  top: -50%;
  right: -10%;
  width: 300px;
  height: 300px;
  background: rgba(255,255,255,0.1);
  border-radius: 50%;
  z-index: 1;
}

/* ── Carte Stepper ─────────────────────────────────────── */
.form-container {
  max-width: 900px;
  margin: 24px auto 0;
}

.add-product-card {
  border-radius: 18px !important;
  overflow: hidden;
}

/* Progress bar arrondie */
.app-pl >>> .v-progress-linear__bar,
.app-pl >>> .v-progress-linear__background {
  border-radius: 6px;
}

/* Step pills */
.step-item { opacity: 0.45; transition: opacity 0.25s ease; }
.step-item.step-active,
.step-item.step-done { opacity: 1; }

.step-circle {
  width: 38px; height: 38px;
  border-radius: 50%;
  background: #e0e0e0;
  font-size: 14px; line-height: 38px;
  text-align: center;
  color: #757575;
  transition: background 0.25s ease, color 0.25s ease;
  flex-shrink: 0;
  margin-bottom: 4px;
}
.step-circle .v-icon {
  vertical-align: middle;
}
.step-active .step-circle {
  background: #667eea;
  color: #fff;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #667eea;
}
.step-done .step-circle {
  background: #43a047;
  color: #fff;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #43a047;
}

.step-label {
  font-size: 0.68rem;
  font-weight: 600;
  color: #9e9e9e;
  white-space: nowrap;
}
.step-active .step-label  { color: #667eea; }
.step-done   .step-label  { color: #43a047; }

@media (max-width: 600px) {
  .step-label { display: none; }
}

/* ── Corps d'étape ─────────────────────────────────────── */
.step-body {
  max-width: 760px;
  margin: 0 auto;
}

/* ── Media zones ───────────────────────────────────────── */
.media-zone {
  border: 1.5px dashed #bdbdbd;
  background: #fafafa;
  border-radius: 12px;
  transition: border-color 0.2s ease;
}
.media-zone--error { border-color: #f44336 !important; }

.cover-preview-wrap { position: relative; display: inline-block; width: 100%; }
.cover-remove-btn {
  position: absolute !important;
  top: -8px; right: -8px;
}
.cover-label {
  position: absolute;
  bottom: 8px;
  left: 8px;
}
.extra-thumb-wrap { position: relative; display: inline-block; }
.thumb-remove-btn {
  position: absolute !important;
  top: -6px; right: -6px;
  width: 22px !important; height: 22px !important;
  min-width: 0 !important;
}

.hidden-input { display: none; }

.rounded-corners { border-radius: 8px; }

/* ── Alerts margins / commission ───────────────────────── */
.margin-alert { margin-bottom: 0 !important; }
.margin-alert .v-alert__content { font-size: 0.85rem; }
.commission-alert { margin-bottom: 0 !important; }
.commission-alert .v-alert__content { font-size: 0.85rem; }

/* ── Rich editor ───────────────────────────────────────── */
.rich-editor-wrapper {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  background: white;
}

.editor-toolbar {
  background: #f8f9fa;
  padding: 4px 8px;
  border-bottom: 1px solid #e0e0e0;
}

.rich-editor {
  min-height: 180px;
  padding: 16px;
  outline: none;
  font-size: 15px;
  line-height: 1.6;
}

.rich-editor:empty:before {
  content: attr(placeholder);
  color: #a0aec0;
  pointer-events: none;
}

.editor-footer {
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
}

/* ── Review ────────────────────────────────────────────── */
.review-title { font-size: 0.85rem; }

/* ── Boutons pill ──────────────────────────────────────── */
.pill-btn { border-radius: 24px !important; }

/* ── Transition d'étape ────────────────────────────────── */
.slide-fade-enter-active,
.slide-fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.slide-fade-enter  { opacity: 0; transform: translateX(18px); }
.slide-fade-leave-to { opacity: 0; transform: translateX(-18px); }
</style>